import React, { useCallback, useMemo, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FieldServicesAppRoutes } from "../lib/constants";
import {
  PageContainer,
  PageAppBar,
  PageRoot,
  ErrorCard,
  ContentSkeleton,
} from "../components/ui";
import { useSelectedWell } from "../hooks/useSelectedWell";
import { useDynamicForm } from "../hooks/useDynamicForm";
import { useAxiosInstance } from "../hooks/useAxiosInstance";
import { waterLevelsFormConfig } from "../formConfigs/waterLevelsFormConfig";
import { DynamicForm } from "../forms/DynamicForm";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../../AppProvider";

const IS_NEW_RECORD = true;

const WaterLevelsPage = function () {
  const { user } = useAuth0();
  const { doToast } = useApp();
  const axiosInstance = useAxiosInstance();

  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { data, isFetching, error, refetch } = useSelectedWell(id);
  const wellNdx = data?.well_ndx || null;

  const queryParams = new URLSearchParams(location.search);
  const redirectPath = queryParams.get("redirect");

  const handleRedirect = () =>
    history.push(redirectPath || FieldServicesAppRoutes.WELL_ACTIONS(id));

  const config = useMemo(() => waterLevelsFormConfig(user?.sub), [user]);

  const {
    isLoading: isLoadingFormData,
    error: formDataError,
    refetchLookups,
    formMethods,
    ...rest
  } = useDynamicForm({
    ndx: "",
    isNewRecord: IS_NEW_RECORD,
    config: config,
    endpoints: {
      submit: `/well-actions/${wellNdx}/water-levels`,
    },
    handleRedirect,
  });

  const [isLoadingLastMP, setIsLoadingLastMP] = useState(false);

  const handleUseLastMP = useCallback(async () => {
    if (!wellNdx) return;
    setIsLoadingLastMP(true);
    try {
      const { data } = await axiosInstance.get(
        `/well-actions/${wellNdx}/water-levels/last-mp`
      );
      if (data.wl_meas_point_height_in !== null) {
        formMethods.setValue(
          "wl_meas_point_height_in",
          data.wl_meas_point_height_in,
          { shouldDirty: true }
        );
        doToast(
          "success",
          `Loaded last MP: ${data.wl_meas_point_height_in} inches`
        );
      } else {
        doToast(
          "warning",
          "No previous measurement point height found for this well."
        );
      }
    } catch (err) {
      console.error("Error fetching last MP:", err);
      doToast("error", "Failed to fetch last measurement point.");
    } finally {
      setIsLoadingLastMP(false);
    }
  }, [wellNdx, axiosInstance, formMethods, doToast]);

  const actions = useMemo(
    () => ({
      wl_meas_point_height_in: {
        label: "Use Last MP",
        tooltip:
          "Use the measurement point height from the most recent water level record",
        onClick: handleUseLastMP,
        loading: isLoadingLastMP,
      },
    }),
    [handleUseLastMP, isLoadingLastMP]
  );

  const anyError = error || formDataError;
  const loading = isFetching || isLoadingFormData;

  const retryActions = [refetch, refetchLookups].filter(Boolean);

  return (
    <PageRoot>
      <Helmet title="Water Levels | Field Services" />

      <PageAppBar
        title={
          <>
            <strong>New GW Level Entry:</strong> {id}
          </>
        }
        buttonPath={redirectPath || FieldServicesAppRoutes.WELL_ACTIONS(id)}
        buttonLabel={redirectPath ? "Back" : "Well Actions"}
      />

      <PageContainer>
        {anyError ? (
          <ErrorCard
            onRetry={() => Promise.all(retryActions.map((action) => action()))}
            isFetching={loading}
          />
        ) : loading ? (
          <ContentSkeleton />
        ) : (
          <DynamicForm
            config={config}
            formMethods={formMethods}
            actions={actions}
            {...rest}
          />
        )}
      </PageContainer>
    </PageRoot>
  );
};

export default WaterLevelsPage;
